/**
 * 이미지와 폴리곤 동기화 관련 기능을 담당하는 클래스
 */
import { imagePropertyUtils } from '../polygonToImageCalculator.js';

export class ImagePolygonSynchronizer {
  /**
   * 폴리곤 지오메트리와 이미지를 동기화
   * @param {GeoImageSource} imageSource - GeoImageSource
   * @param {ol/geom/Geometry} geometry - 폴리곤 지오메트리
   * @param {Object} originalPolygonSize - 원본 폴리곤 크기 {width, height}
   * @param {Object} imageSize - 이미지 크기 {width, height}
   * @param {ol/geom/Geometry} originalGeometry - 원본 폴리곤 지오메트리
   * @returns {boolean} 동기화 성공 여부
   */
  syncImageWithPolygonGeometry(imageSource, geometry, originalPolygonSize, imageSize, originalGeometry) {
    if (!imageSource || !geometry || !originalPolygonSize || !imageSize) {
      console.warn('ImagePolygonSynchronizer: 유효하지 않은 매개변수');
      return false;
    }

    try {
      // 폴리곤 좌표 추출
      const coordinates = geometry.getCoordinates();
      if (!coordinates || !coordinates[0] || coordinates[0].length < 4) {
        console.warn('ImagePolygonSynchronizer: 유효하지 않은 좌표 데이터');
        return false;
      }

      const polygonCoordinates = coordinates[0];

      // polygonToImageCalculator를 사용하여 이미지 속성 계산
      const imageProperties = imagePropertyUtils.calculateImageProperties(polygonCoordinates, imageSize);

      if (!imageProperties) {
        console.error('ImagePolygonSynchronizer: 이미지 속성 계산 실패');
        return false;
      }

      console.log('=== 이미지-폴리곤 동기화 ===');
      console.log('이미지 원본 크기 (W x H):', imageSize.width, 'x', imageSize.height);
      console.log('원본 폴리곤 크기 (W x H):', originalPolygonSize.width, 'x', originalPolygonSize.height);
      console.log('계산된 이미지 중심점:', imageProperties.center);
      console.log('계산된 이미지 스케일 (X, Y):', imageProperties.scale[0].toFixed(6), 'x', imageProperties.scale[1].toFixed(6));
      console.log('계산된 회전 각도:', imageProperties.rotation * 180 / Math.PI, '도');

      // GeoImage 업데이트
      this.updateImageCenter(imageSource, imageProperties.center);
      this.updateImageScale(imageSource, imageProperties.scale);
      this.updateImageRotation(imageSource, imageProperties.rotation);

      return true;
    } catch (error) {
      console.error('ImagePolygonSynchronizer: 동기화 실패:', error);
      return false;
    }
  }

  /**
   * 원본 폴리곤 대비 현재 폴리곤의 스케일 계산 (중심점 기반)
   * @param {ol/geom/Geometry} originalGeometry - 원본 폴리곤 지오메트리
   * @param {ol/geom/Geometry} currentGeometry - 현재 폴리곤 지오메트리
   * @returns {number} 스케일 비율
   */
  calculatePolygonScaleFromOriginal(originalGeometry, currentGeometry) {
    if (!originalGeometry) return 1;

    // ✅ 중심점 기반 스케일 계산 (좌표 시작위치에 무관)
    const originalCenter = this.calculatePolygonCenter(originalGeometry);
    const currentCenter = this.calculatePolygonCenter(currentGeometry);

    // 원본 폴리곤의 중심점에서 가장 먼 꼭지점까지의 거리
    const originalMaxDistance = this.calculateMaxDistanceFromCenter(originalGeometry, originalCenter);

    // 현재 폴리곤의 중심점에서 가장 먼 꼭지점까지의 거리
    const currentMaxDistance = this.calculateMaxDistanceFromCenter(currentGeometry, currentCenter);

    // 스케일 비율 계산
    const scale = currentMaxDistance / originalMaxDistance;

    console.log('=== 중심점 기반 스케일 계산 ===');
    console.log('원본 중심점:', originalCenter);
    console.log('현재 중심점:', currentCenter);
    console.log('원본 최대 거리:', originalMaxDistance);
    console.log('현재 최대 거리:', currentMaxDistance);
    console.log('계산된 스케일:', scale);

    return scale;
  }

  /**
   * 폴리곤의 중심점 계산
   * @param {ol/geom/Geometry} geometry - 폴리곤 지오메트리
   * @returns {Array} 중심점 [x, y]
   */
  calculatePolygonCenter(geometry) {
    const extent = geometry.getExtent();
    return [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
  }

  /**
   * 중심점에서 가장 먼 꼭지점까지의 거리 계산
   * @param {ol/geom/Geometry} geometry - 폴리곤 지오메트리
   * @param {Array} center - 중심점 [x, y]
   * @returns {number} 최대 거리
   */
  calculateMaxDistanceFromCenter(geometry, center) {
    const coordinates = geometry.getCoordinates()[0];
    let maxDistance = 0;

    coordinates.forEach(coord => {
      const dx = coord[0] - center[0];
      const dy = coord[1] - center[1];
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance > maxDistance) {
        maxDistance = distance;
      }
    });

    return maxDistance;
  }

  /**
   * 이미지 중심점 업데이트
   * @param {GeoImageSource} imageSource - GeoImageSource
   * @param {Array} newCenter - 새로운 중심점 [lng, lat]
   */
  updateImageCenter(imageSource, newCenter) {
    if (!imageSource || !newCenter) {
      console.warn('ImagePolygonSynchronizer: 유효하지 않은 매개변수');
      return;
    }

    imageSource.setCenter(newCenter);
    console.log('ImagePolygonSynchronizer: 이미지 중심점 업데이트 완료:', newCenter);
  }

  /**
   * 이미지 스케일 업데이트
   * @param {GeoImageSource} imageSource - GeoImageSource
   * @param {Array} newScale - 새로운 스케일 [x, y]
   */
  updateImageScale(imageSource, newScale) {
    if (!imageSource || !newScale) {
      console.warn('ImagePolygonSynchronizer: 유효하지 않은 매개변수');
      return;
    }

    imageSource.setScale(newScale);
    console.log('ImagePolygonSynchronizer: 이미지 스케일 업데이트 완료:', newScale);
  }

  /**
   * 이미지 회전 업데이트
   * @param {GeoImageSource} imageSource - GeoImageSource
   * @param {number} newRotation - 새로운 회전각도 (라디안)
   */
  updateImageRotation(imageSource, newRotation) {
    if (!imageSource) {
      console.warn('ImagePolygonSynchronizer: 유효하지 않은 GeoImageSource');
      return;
    }

    imageSource.setRotation(newRotation);
    console.log('ImagePolygonSynchronizer: 이미지 회전 업데이트 완료:', newRotation * 180 / Math.PI, '도');
  }

  /**
   * 이미지에서 폴리곤 업데이트
   * @param {GeoImageSource} imageSource - GeoImageSource
   * @param {ol/Feature} polygonFeature - 폴리곤 피처
   */
  updatePolygonFromImage(imageSource, polygonFeature) {
    if (!imageSource || !polygonFeature) {
      console.warn('ImagePolygonSynchronizer: 유효하지 않은 매개변수');
      return;
    }

    try {
      const center = imageSource.getCenter();
      const scale = imageSource.getScale();
      const rotation = imageSource.getRotation();

      // 이미지 정보로부터 폴리곤 좌표 계산
      const polygonCoordinates = this.calculatePolygonCoordinatesFromImage(center, scale, rotation);

      // 폴리곤 지오메트리 업데이트
      const newGeometry = new (require('ol/geom/Polygon').default)([polygonCoordinates]);
      polygonFeature.setGeometry(newGeometry);

      console.log('ImagePolygonSynchronizer: 폴리곤 업데이트 완료');
    } catch (error) {
      console.error('ImagePolygonSynchronizer: 폴리곤 업데이트 실패:', error);
    }
  }

  /**
   * 이미지 정보로부터 폴리곤 좌표 계산
   * @param {Array} center - 이미지 중심점 [lng, lat]
   * @param {Array} scale - 이미지 스케일 [x, y]
   * @param {number} rotation - 이미지 회전각도 (라디안)
   * @returns {Array} 폴리곤 좌표 배열
   */
  calculatePolygonCoordinatesFromImage(center, scale, rotation) {
    // 기본 사각형 좌표 (중심점 기준)
    const halfWidth = scale[0] / 2;
    const halfHeight = scale[1] / 2;

    const baseCoordinates = [
      [-halfWidth, -halfHeight],
      [halfWidth, -halfHeight],
      [halfWidth, halfHeight],
      [-halfWidth, halfHeight],
      [-halfWidth, -halfHeight] // 닫힌 폴리곤
    ];

    // 회전 적용
    const rotatedCoordinates = baseCoordinates.map(coord => {
      const x = coord[0];
      const y = coord[1];
      const rotatedX = x * Math.cos(rotation) - y * Math.sin(rotation);
      const rotatedY = x * Math.sin(rotation) + y * Math.cos(rotation);
      return [rotatedX, rotatedY];
    });

    // 중심점 이동 적용
    const finalCoordinates = rotatedCoordinates.map(coord => [
      coord[0] + center[0],
      coord[1] + center[1]
    ]);

    return finalCoordinates;
  }
}
